import { PLAN_CATALOG_COPY } from "./plan-catalog.copy";
import type { Plan } from "./plan.schema";

/**
 * A vitrine deixou de escolher card por nome ou por `signable`.
 *
 * A seção de preços escolhia o card pago com `filteredPlans.find(p => p.signable)`
 * — o **primeiro** assinável da lista — e o gratuito por `plan.name === "Free"`.
 * Ao cadastrar um terceiro plano, ele ocupou o slot único e o plano de cima
 * sumiu da home. Aqui a ordem vem do preço, então um plano novo aparece sozinho,
 * na posição certa da escada.
 *
 * Plano sob consulta não tem valor publicado, então não entra na comparação
 * numérica — vai para o fim, que é onde a escada comercial o coloca.
 *
 * A ordenação é estável: dois planos de mesmo valor preservam a ordem de
 * entrada, para que a página não troque as colunas de lugar entre renders.
 */
export function orderPlansForShowcase(plans: Plan[]): Plan[] {
  return [...plans].sort((a, b) => {
    const aHidden = a.priceOnRequest || a.unitValueNumber === null;
    const bHidden = b.priceOnRequest || b.unitValueNumber === null;

    if (aHidden !== bHidden) return aHidden ? 1 : -1;
    if (aHidden && bHidden) return 0;

    return (a.unitValueNumber ?? 0) - (b.unitValueNumber ?? 0);
  });
}

/**
 * O destaque é decisão do admin, pelo campo `popular` — nenhum plano marcado,
 * nenhum destaque, e a seção simplesmente não realça coluna nenhuma.
 *
 * Com mais de um marcado vence o primeiro da lista recebida. Como quem chama
 * passa a lista já ordenada, na prática vence o mais barato entre os marcados.
 * Não há desempate por preço nem por nome: dois planos populares é erro de
 * cadastro, e inventar critério aqui só esconderia o erro.
 */
export function findHighlighted(plans: Plan[]): Plan | undefined {
  return plans.find((plan) => plan.popular);
}

/**
 * Dois caminhos chegam no mesmo lugar. O primeiro é a decisão comercial
 * (`priceOnRequest`). O segundo é defesa: se o plano não tem os valores que o
 * próprio modelo de precificação exige, não existe preço para anunciar — e um
 * `?? 0` transformaria "ainda não sei" em "é de graça", uma promessa pública
 * que o checkout não cumpre.
 */
export function hidesPrice(plan: Plan): boolean {
  if (plan.priceOnRequest) {
    return true;
  }
  if (plan.pricingModel === "BASE_PLUS_ADDITIONAL") {
    return (
      plan.baseValueNumber == null || plan.additionalUnitValueNumber == null
    );
  }
  return plan.unitValueNumber == null;
}

export function resolvePriceLabel(plan: Plan): string {
  return plan.priceOnRequestLabel?.trim() || PLAN_CATALOG_COPY.priceOnRequestLabel;
}

export function resolveCtaLabel(plan: Plan): string {
  return plan.contactCtaLabel?.trim() || PLAN_CATALOG_COPY.contactCtaLabel;
}

/**
 * Grátis é ausência de preço **por decisão comercial**, não ausência de dado.
 *
 * Sob consulta o valor também chega nulo, e testar só `unitValueNumber` daria
 * ao Enterprise o selo "100% Free" e o CTA de criar conta — por isso a checagem
 * de `priceOnRequest` vem antes.
 */
export function isFreePlan(plan: Plan): boolean {
  return !plan.priceOnRequest && !plan.unitValueNumber;
}
